import { useState } from 'react';
import Icon from '../../../components/AppIcon';

interface RegulatoryBasisProps {
  regulations: string[];
  complianceNotes: string[];
}

const RegulatoryBasis = ({ regulations, complianceNotes }: RegulatoryBasisProps) => {
  const [expandedRegulations, setExpandedRegulations] = useState<Set<number>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');

  const toggleRegulation = (index: number) => {
    const newExpanded = new Set(expandedRegulations);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRegulations(newExpanded);
  };

  const filteredRegulations = regulations.filter((reg) =>
    reg.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Base Regulatória</h3>

        <div className="relative mb-4">
          <Icon
            name="Search"
            size={18}
            color="var(--color-muted-foreground)"
            className="absolute left-3 top-1/2 -translate-y-1/2"
          />
          <input
            type="text"
            placeholder="Buscar em regulamentações..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        <div className="space-y-3">
          {filteredRegulations.map((regulation, index) => {
            const isExpanded = expandedRegulations.has(index);
            const preview = regulation.length > 150 ? regulation.substring(0, 150) + '...' : regulation;

            return (
              <div key={index} className="border border-border rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleRegulation(index)}
                  className="w-full flex items-start gap-3 p-4 text-left hover:bg-muted transition-colors"
                >
                  <Icon
                    name="Scale"
                    size={18}
                    color="var(--color-primary)"
                    className="mt-0.5 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground leading-relaxed">
                      {isExpanded ? regulation : preview}
                    </p>
                  </div>
                  <Icon
                    name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                    size={18}
                    color="var(--color-muted-foreground)"
                    className="flex-shrink-0"
                  />
                </button>
              </div>
            );
          })}
        </div>

        {filteredRegulations.length === 0 && (
          <div className="text-center py-8">
            <Icon name="FileX" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
            <p className="text-muted-foreground">
              {searchTerm ? 'Nenhuma regulamentação encontrada' : 'Nenhuma regulamentação registrada'}
            </p>
          </div>
        )}
      </div>

      {complianceNotes.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">Notas de Conformidade</h3>
          <div className="space-y-3">
            {complianceNotes.map((note, index) => (
              <div key={index} className="flex items-start gap-3 p-4 bg-accent/5 border border-accent/20 rounded-lg">
                <Icon
                  name="AlertCircle"
                  size={18}
                  color="var(--color-accent)"
                  className="mt-0.5 flex-shrink-0"
                />
                <p className="text-sm text-foreground leading-relaxed">{note}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RegulatoryBasis;